import { useRef, useState } from 'react';
import {
  Box, Button, Typography, CircularProgress, Chip, Avatar, TextField,
} from '@mui/material';
import { CloudUpload, Image as ImageIcon } from '@mui/icons-material';
import { uploadFormFile } from '../../../services/uploadService';
import { resolveAssetUrl } from '../../../utils/assetUrl';
import { validateImageFile, type ImageConstraints } from '../../../utils/imageValidation';
import { useI18n } from '../../../i18n/I18nProvider';
import { translateUiText } from '../../../i18n/translateUiText';

type Props = {
  label: string;
  value: string;
  onChange: (url: string) => void;
  folder: string;
  accept?: string;
  helperText?: string;
  required?: boolean;
  showUrlField?: boolean;
  size?: 'small' | 'medium';
  imageConstraints?: ImageConstraints;
};

export default function FormFileUpload({
  label,
  value,
  onChange,
  folder,
  accept = 'image/png,image/jpeg,image/webp,image/gif',
  helperText,
  required,
  showUrlField = false,
  size = 'small',
  imageConstraints,
}: Props) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [storage, setStorage] = useState<'local' | 's3' | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  const previewUrl = resolveAssetUrl(value);
  const isImage = !accept || accept.includes('image');

  const constraintHint = imageConstraints?.minWidth && imageConstraints?.minHeight
    ? `${imageConstraints.minWidth}×${imageConstraints.minHeight}px min`
    : null;

  const handleFile = async (file?: File) => {
    if (!file) return;
    setError('');
    setDimensions(null);

    if (imageConstraints && isImage) {
      const check = await validateImageFile(file, imageConstraints);
      if (!check.valid) {
        setError(check.error || 'Image does not meet requirements');
        return;
      }
      if (check.width && check.height) {
        setDimensions({ width: check.width, height: check.height });
      }
    }

    setUploading(true);
    try {
      const result = await uploadFormFile(file, folder);
      onChange(result.url);
      setStorage(result.storage);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Upload failed';
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {translateUiText(label, t)}{required ? ' *' : ''}
      </Typography>
      {(helperText || constraintHint) && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {helperText ? translateUiText(helperText, t) : ''}
          {helperText && constraintHint ? ' · ' : ''}
          {constraintHint}
          {imageConstraints?.aspectRatio ? ` · Ratio ${imageConstraints.aspectRatio}` : ''}
        </Typography>
      )}

      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {isImage && (
          previewUrl ? (
            <Avatar
              key={previewUrl}
              src={previewUrl}
              variant="rounded"
              imgProps={{ style: { objectFit: 'contain' } }}
              sx={{
                width: 72,
                height: 72,
                bgcolor: 'grey.100',
                border: 1,
                borderColor: 'divider',
                '& img': { objectFit: 'contain', p: 0.5 },
              }}
            >
              <ImageIcon />
            </Avatar>
          ) : (
            <Box
              sx={{
                width: 72, height: 72, borderRadius: 2, border: 1, borderColor: 'divider',
                display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50',
              }}
            >
              <ImageIcon color="disabled" />
            </Box>
          )
        )}

        <Box flex={1} minWidth={160}>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            hidden
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button
            variant="outlined"
            size={size}
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading
              ? translateUiText('Uploading...', t)
              : value
                ? translateUiText('Replace file', t)
                : translateUiText('Upload file', t)}
          </Button>
          {storage && (
            <Chip
              size="small"
              label={storage === 's3' ? 'Stored on S3' : 'Stored locally'}
              color={storage === 's3' ? 'primary' : 'default'}
              sx={{ ml: 1 }}
            />
          )}
          {dimensions && (
            <Chip
              size="small"
              label={`${dimensions.width}×${dimensions.height}px`}
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
            PNG, JPEG, or WebP. Dimensions validated before upload.
          </Typography>
        </Box>
      </Box>

      {showUrlField && (
        <TextField
          fullWidth
          size={size}
          label="File URL"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{ mt: 1.5 }}
        />
      )}

      {error && (
        <Typography variant="caption" color="error" display="block" mt={0.5}>{error}</Typography>
      )}
    </Box>
  );
}
